/**
 * SUB-5.6 · FR47. The filter state of the merged task list, and how it reaches
 * the server.
 *
 * ⚠️ THIS DECIDES WHAT IS OFFERED. IT BOUNDS NOTHING. The accounts a task list
 * may span is `merged_scope_tenant_ids()` evaluated inside `tasks_select`, and
 * nothing here may ever be why a row is absent. The twin of
 * `projectListFilters.ts`, deliberately, rather than a second idiom.
 *
 * ⚠️ EVERY FILTER IS APPLIED TO THE QUERY (D31). No `Array.filter` over fetched
 * rows lives in this module, and adding one would put a second predicate on
 * membership where a reviewer cannot tell it from the first.
 *
 * ---------------------------------------------------------------------------
 * ⚠️ F-61 WAS FILED TOO STRONGLY AND IS CORRECTED HERE.
 *
 * It said every read in `taskService` is scoped to the caller, so no surface
 * could answer M3's question. **`getProjectTasks` is not self-scoped.** It
 * carries no `user_id` or `assignee_id` predicate, and in merged scope
 * `resolveTaskScope` hands it a null tenant, so the "Assigned to projects" tab
 * already shows every task in the subtree to the four privileged roles.
 *
 * What is actually missing is narrower, and measured 2026-08-16:
 *
 *   open tasks live                       130
 *   of those, reachable by that tab       128
 *   ⚠️ open tasks with NO project_id        2
 *
 * `getProjectTasks` requires `project_id IS NOT NULL`. M3 counts every open
 * task. So a drill-down onto that tab would land on a list **two rows short of
 * the figure that opened it**, which is precisely the tile-disagrees-with-its-
 * list failure SUB-5.5 exists to prevent. Hence `getPortfolioTasks`, which is
 * that read without the project requirement, and this module, which is the
 * vocabulary neither side had.
 */

/** The slice of a Supabase query builder this module uses. Same shape as `projectListFilters.ts`. */
export interface TaskFilterableQuery {
  eq(column: string, value: unknown): TaskFilterableQuery;
  is(column: string, value: unknown): TaskFilterableQuery;
  neq(column: string, value: unknown): TaskFilterableQuery;
  or(filters: string): TaskFilterableQuery;
}

export interface TaskListFilters {
  /** FR35. One account within the merged set, or null for all of them. */
  accountTenantId: string | null;
  /**
   * `tasks.assignee_id`. ⚠️ The empty string is NOT null here: `unassigned` is a
   * real answer M3 has to be able to open, because D50 groups unattributable
   * work rather than dropping it.
   */
  assigneeId: string | null;
  /** True for the drill-down: M3 counts open work, so its list shows open work. */
  openOnly: boolean;
}

export const EMPTY_TASK_LIST_FILTERS: TaskListFilters = {
  accountTenantId: null,
  assigneeId: null,
  openOnly: false,
};

/**
 * The query-string names, and the ONLY ones the task list reads.
 *
 * ⚠️ `taskId` is NOT here and must not be. `/app/tasks` has honoured it since
 * before merged scope existed and something else owns it; this module carries
 * every param through that it does not own, exactly as the Projects list does.
 */
export const TASK_FILTER_PARAMS = {
  view: 'view',
  account: 'account',
  assignee: 'assignee',
  open: 'open',
} as const;

/** The view a drill-down asks for. Anything else leaves the tab alone. */
export const PORTFOLIO_TASK_VIEW = 'portfolio';

/**
 * M3 groups work it cannot attribute to a person under its account (D50), so
 * "show me that group" has to be expressible. A bare `?assignee=` would decode
 * as absent and open the whole account.
 */
export const UNASSIGNED_ASSIGNEE = 'unassigned';

/**
 * Apply the filter state to a `tasks` query.
 *
 * ⚠️ `openOnly` IS TWO CLAUSES, NOT ONE. A task is done when its status says so
 * OR when `completed_at` is set, and the two disagree on live rows. Testing only
 * the status would count a completed task as open, which overstates the very
 * figure M3 exists to make actionable. This is the query-side spelling of the
 * portfolio rule.
 *
 * ⚠️ **AND A NULL STATUS IS OPEN, WHICH `neq` DOES NOT SAY.** An unlabelled task
 * is still work somebody is holding, so a null status counts as open. (That rule
 * was `portfolioMetrics.ts:teamLoad` until SUB-5.25 removed it as dead code; the
 * rule is stated here rather than cited so it does not depend on that function.)
 * In SQL `status <> 'completed'`
 * evaluates to NULL for a NULL status under three-valued logic and the row is
 * filtered OUT, so the claim above that this is "the query-side spelling of"
 * the metric was false in exactly the direction that makes a list shorter than
 * the figure that opened it. `tasks.status` is nullable (`information_schema`),
 * and 0 rows are null today, so this was latent rather than live.
 */
export function applyTaskListFilters<Q>(query: Q, filters: TaskListFilters): Q {
  let q = query as unknown as TaskFilterableQuery;

  if (filters.accountTenantId) {
    q = q.eq('tenant_id', filters.accountTenantId);
  }

  if (filters.assigneeId === UNASSIGNED_ASSIGNEE) {
    q = q.is('assignee_id', null);
  } else if (filters.assigneeId) {
    q = q.eq('assignee_id', filters.assigneeId);
  }

  if (filters.openOnly) {
    q = q.or('status.is.null,status.neq.completed').is('completed_at', null);
  }

  return q as unknown as Q;
}

/** How many filters are applied, for the badge. `openOnly` is a filter like any other. */
export function activeTaskFilterCount(filters: TaskListFilters): number {
  let count = 0;
  if (filters.accountTenantId) count += 1;
  if (filters.assigneeId) count += 1;
  if (filters.openOnly) count += 1;
  return count;
}

/**
 * The reading direction. A drill-down link becomes filter state.
 *
 * ⚠️ An unusable value reads as NO filter rather than as a guess, the same way
 * `projectListFiltersFromParams` does and for the same reason: a visibly
 * unfiltered list is a better failure than a plausible list that is the wrong
 * one.
 */
export function taskListFiltersFromParams(params: URLSearchParams): TaskListFilters {
  const p = TASK_FILTER_PARAMS;
  return {
    accountTenantId: params.get(p.account) || null,
    assigneeId: params.get(p.assignee) || null,
    openOnly: params.get(p.open) === '1',
  };
}

/** The writing direction. Filter state becomes a shareable link. */
export function taskListFiltersToParams(filters: TaskListFilters): URLSearchParams {
  const p = TASK_FILTER_PARAMS;
  const params = new URLSearchParams();
  if (filters.accountTenantId) params.set(p.account, filters.accountTenantId);
  if (filters.assigneeId) params.set(p.assignee, filters.assigneeId);
  if (filters.openOnly) params.set(p.open, '1');
  return params;
}

/** Whether a URL is asking for the portfolio view rather than one of the personal ones. */
export function wantsPortfolioView(params: URLSearchParams): boolean {
  return params.get(TASK_FILTER_PARAMS.view) === PORTFOLIO_TASK_VIEW;
}

/**
 * Drop the filters whose meaning depends on which account you are in.
 *
 * The assignee is the one with this property, for the same reason it is on the
 * Projects list: `users_select` is single-tenant, so a person chosen in one
 * scope may not be nameable in the next, and leaving it applied would be an
 * invisible predicate on a list that says it spans the whole portfolio.
 *
 * ⚠️ `unassigned` is NOT cleared. It names no person, so it means the same thing
 * in every account and survives a scope change intact.
 */
export function clearAccountScopedTaskFilters(filters: TaskListFilters): TaskListFilters {
  if (!filters.assigneeId || filters.assigneeId === UNASSIGNED_ASSIGNEE) return filters;
  return { ...filters, assigneeId: null };
}
